import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Edit, X, GripVertical, Eye, EyeOff, Search, CheckSquare, Square, History, ChevronUp, ChevronDown, Lock } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../../services/api';
import ConfirmModal from '../../components/ui/ConfirmModal';
import CustomSelect from '../../components/ui/CustomSelect';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../context/OrganizationContext';

// ── Reusable components & bits ──────────────────────────────────────────────
const labelCls = "block text-[11px] font-bold text-muted uppercase tracking-wider mb-1.5";
const inputCls = "w-full px-4 py-2.5 rounded-xl bg-surface-2 border border-border text-primary text-sm focus:outline-none focus:border-brand transition-colors";

function Button({ children, isLoading, ...props }) {
    return (
        <button
            {...props}
            disabled={isLoading || props.disabled}
            className={`flex items-center justify-center gap-2 bg-brand text-inverse hover:bg-brand-hover font-bold rounded-xl transition-all disabled:opacity-50 ${props.className || ''}`}
        >
            {isLoading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : children}
        </button>
    );
}

function Toggle({ label, k, form, setForm }) {
    const checked = form[k];
    return (
        <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
                <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={checked}
                    onChange={e => setForm(f => ({ ...f, [k]: e.target.checked }))}
                />
                <div className="w-10 h-5 bg-border rounded-full peer peer-checked:bg-brand
                after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white
                after:rounded-full after:h-4 after:w-4 after:transition-all
                peer-checked:after:translate-x-5" />
            </label>
            <span className="text-sm font-medium text-secondary">{label}</span>
        </div>
    );
}

const EMPTY = {
    category_id: '',
    name_ru: '', name_uz: '', name_en: '',
    long_name_ru: '', long_name_uz: '', long_name_en: '',
    description_ru: '', description_uz: '', description_en: '',
    visiology_url: '',
    visiology_mobile_url: '',
    visiology_url_light: '',
    visiology_mobile_url_light: '',
    update_interval: '',
    order: 0,
    is_visible: true,
    has_online: false,
    has_dashboard: true,
    user_ids: []
};

export default function SubcategoriesPage() {
    const { t, i18n } = useTranslation();
    const { user: currentUser } = useAuth();
    const { currentOrg, currentOrgId } = useOrganization();
    const [subcats, setSubcats] = useState([]);
    const [cats, setCats] = useState([]);
    const [users, setUsers] = useState([]);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editId, setEditId] = useState(null);
    const [togglingId, setTogglingId] = useState(null);
    const navigate = useNavigate();
    const [form, setForm] = useState(EMPTY);
    const [formError, setFormError] = useState('');

    const [filterCat, setFilterCat] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    const intervalH = form.update_interval ? Math.floor(form.update_interval / 3600) : '';
    const intervalM = form.update_interval ? Math.floor((form.update_interval % 3600) / 60) : '';

    const handleIntervalChange = (type, val) => {
        const v = val === '' ? 0 : parseInt(val, 10);
        let h = type === 'h' ? v : (intervalH || 0);
        let m = type === 'm' ? v : (intervalM || 0);
        
        const total = h * 3600 + m * 60;
        setForm(f => ({ ...f, update_interval: total || '' }));
    };

    // Modal state for deletion
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => { load(); }, [currentOrgId]);

    const load = async () => {
        setLoading(true);
        try {
            const orgParam = currentOrgId ? { organization_id: currentOrgId } : {};
            const [scRes, cRes, uRes] = await Promise.all([
                api.get('/admin/subcategories', { params: orgParam }),
                api.get('/admin/categories', { params: orgParam }),
                api.get('/admin/users')
            ]);
            setSubcats(scRes.data);
            setCats(cRes.data);
            setUsers(uRes.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const formatInterval = (sec) => {
        if (!sec) return '—';
        const h = Math.floor(sec / 3600);
        const m = Math.floor((sec % 3600) / 60);
        const s = sec % 60;
        
        let parts = [];
        if (h > 0) parts.push(`${h}${i18n.language === 'uz' ? 's' : 'ч'}`);
        if (m > 0) parts.push(`${m}${i18n.language === 'uz' ? 'm' : 'м'}`);
        if (s > 0) parts.push(`${s}${i18n.language === 'uz' ? 's' : 'с'}`);
        return parts.join(' ');
    };

    const field = (k) => ({
        value: form[k] ?? '',
        onChange: e => setForm(f => ({ ...f, [k]: e.target.value }))
    });

    function openCreate() {
        setEditId(null); setForm(EMPTY); setFormError(''); setShowForm(true);
    }

    function openEdit(s) {
        setEditId(s.id);
        setForm({
            category_id: s.category_id,
            name_ru: s.name_ru,
            name_uz: s.name_uz || '',
            long_name_ru: s.long_name_ru || '',
            long_name_uz: s.long_name_uz || '',
            description_ru: s.description_ru || '',
            description_uz: s.description_uz || '',
            visiology_url: s.visiology_url || '',
            visiology_mobile_url: s.visiology_mobile_url || '',
            visiology_url_light: s.visiology_url_light || '',
            visiology_mobile_url_light: s.visiology_mobile_url_light || '',
            update_interval: s.update_interval || '',
            order: s.order || 0,
            is_visible: s.is_visible,
            has_online: s.has_online ?? false,
            has_dashboard: s.has_dashboard ?? true,
            user_ids: s.user_ids || []
        });
        setFormError('');
        setShowForm(true);
        setTimeout(() => { const el = document.getElementById('admin-content'); if (el) el.scrollTo({ top: 0, behavior: 'smooth' }); }, 50);
    }

    async function handleSave(e) {
        e.preventDefault();

        // ── Client-side required-field check ─────────────────────────────────
        const missing = [];
        if (!form.category_id)          missing.push(t('subcategories.category', 'Kategoriya'));
        if (!form.name_ru?.trim())      missing.push(t('categories.name_ru', 'Название (RU)'));
        if (!form.name_uz?.trim())      missing.push(t('categories.name_uz', 'Nomi (UZ)'));
        if (missing.length > 0) {
            setFormError(`${t('users.req_fields')}${missing.join(', ')}`);
            return;
        }
        // ─────────────────────────────────────────────────────────────────────

        setSaving(true);
        setFormError('');
        
        // Convert update_interval to integer or null
        const payload = { ...form };
        if (payload.update_interval === '' || payload.update_interval === null) {
            payload.update_interval = null;
        } else {
            payload.update_interval = parseInt(payload.update_interval, 10);
        }

        try {
            if (editId) await api.put(`/admin/subcategories/${editId}`, payload);
            else await api.post('/admin/subcategories', payload);
            setShowForm(false); load();
        } catch (e) {
            console.error("AXIOS ERROR OBJECT:", e);
            console.error("API INTERCEPTOR MESSAGE:", e.message);
            
            let errorMsg = e.message || 'Error parsing request';
            
            // Map known validation errors to translations using plain message text
            // interceptor format is typically "field_name: validation error message"
            if (errorMsg.toLowerCase().includes("visiology_url") || errorMsg.toLowerCase().includes("visiology") || errorMsg.toLowerCase().includes("simulation.centralasian.uz")) {
                if (errorMsg.includes("http://") || errorMsg.includes("https://")) {
                    errorMsg = `Dashboard Iframe URL: ${t('subcategories.url_error_protocol')}`;
                } else {
                    errorMsg = `Dashboard Iframe URL: ${t('subcategories.url_warn_visiology')}`;
                }
            }
            
            setFormError(errorMsg);
        }
        finally { setSaving(false); }
    }

    function handleDelete(id) {
        setPendingDeleteId(id);
        setIsModalOpen(true);
    }

    async function confirmDelete() {
        if (!pendingDeleteId) return;
        setIsDeleting(true);
        try {
            await api.delete(`/admin/subcategories/${pendingDeleteId}`);
            setIsModalOpen(false);
            setPendingDeleteId(null);
            load();
        } catch (e) {
            alert(e?.response?.data?.detail || e.message);
        } finally {
            setIsDeleting(false);
        }
    }

    // Admin bu dashboardga ruxsatli bo'lishi kerak (tayinlangan yoki yaratgan)
    const hasAccessToSub = (sub) => {
        if (currentUser?.is_superadmin) return true;
        const isCreator = sub.created_by_id === currentUser?.id;
        const isAssigned = currentUser?.subcategories?.some(s => s.id === sub.id);
        return isCreator || isAssigned;
    };

    async function toggleVisible(sub) {
        if (!currentUser?.is_superadmin && !currentUser?.can_edit_subcategories) return;
        if (!hasAccessToSub(sub)) return;
        setTogglingId(sub.id);
        try {
            await api.put(`/admin/subcategories/${sub.id}`, { is_visible: !sub.is_visible });
            await load();
        } catch (e) { alert(e?.response?.data?.detail || e.message); }
        finally { setTogglingId(null); }
    }

    const canEditOrder = currentUser?.is_superadmin || currentUser?.can_edit_subcategory_order;

    async function handleDragEnd(result) {
        if (!result.destination || !canEditOrder) return;

        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;

        if (sourceIndex === destinationIndex) return;

        // Drag-and-drop har doim filterlangan ro'yxat bilan ishlaydi.
        // Xavfsizlik uchun: faqat bir xil category_id li subcategorylarning orderi o'zgartiriladi.
        const newVisible = Array.from(visible);
        const [reorderedItem] = newVisible.splice(sourceIndex, 1);
        newVisible.splice(destinationIndex, 0, reorderedItem);

        // Optimistically update the UI — faqat visible ro'yxatdagi elementlarni yangilash
        setSubcats(prev => {
            const visibleIds = new Set(visible.map(v => v.id));
            let visibleCounter = 0;
            return prev.map(item => {
                if (visibleIds.has(item.id)) {
                    const mappedItem = { ...newVisible[visibleCounter], order: visibleCounter };
                    visibleCounter++;
                    return mappedItem;
                }
                return item;
            });
        });

        // Backendga faqat visible ro'yxatdagi elementlar uchun yangi orderlarni yuborish
        try {
            await Promise.all(
                newVisible.map((item, index) =>
                    api.put(`/admin/subcategories/${item.id}`, { order: index })
                )
            );
        } catch (e) {
            console.error("Failed to update order:", e);
            alert(e?.response?.data?.detail || e.message);
            load(); // Revert on failure
        }
    }

    const visible = useMemo(() => {
        let result = subcats;
        if (filterCat) result = result.filter(s => s.category_id === filterCat);
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            result = result.filter(s =>
                (s.name_ru || '').toLowerCase().includes(q) ||
                (s.name_uz || '').toLowerCase().includes(q)
            );
        }
        return result;
    }, [subcats, filterCat, searchQuery]);

    const isUz = i18n.language === 'uz';

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">{t('subcategories.title')}</h2>
                {(currentUser?.is_superadmin || currentUser?.can_create_subcategories) && (
                    <button
                        onClick={showForm ? () => setShowForm(false) : openCreate}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-colors
                            ${showForm ? 'bg-surface-2 border border-border text-secondary' : 'bg-brand text-inverse hover:bg-brand-hover'}`}
                    >
                        {showForm ? <><X className="w-4 h-4" /> {t('subcategories.cancel')}</> : <><Plus className="w-4 h-4" /> {t('subcategories.add')}</>}
                    </button>
                )}
            </div>

            {/* ── Form ── */}
            {showForm && (
                <div className="bg-surface border border-border rounded-xl p-6 shadow-sm">
                    <h3 className="font-bold text-lg mb-5">{editId ? t('subcategories.edit') : t('subcategories.add')}</h3>
                    {formError && (
                        <div className="mb-5 p-4 rounded-xl bg-error/10 border border-error/20 text-error text-sm font-medium">
                            {typeof formError === 'string' ? formError : JSON.stringify(formError)}
                        </div>
                    )}
                    <form onSubmit={handleSave} className="space-y-5">
                        {/* Row: category */}
                        <div className="space-y-1">
                            <label className={labelCls}>{t('subcategories.category')} *</label>
                            <CustomSelect 
                                value={form.category_id}
                                onChange={val => setForm(p => ({ ...p, category_id: val }))}
                                placeholder={`— ${t('subcategories.select_cat')} —`}
                                options={cats.map(c => ({
                                    value: c.id,
                                    label: i18n.language === 'en' ? (c.name_en || c.name_ru || c.name_uz) : (isUz ? (c.name_uz || c.name_ru) : c.name_ru)
                                }))}
                                required={true}
                            />
                        </div>

                        {/* Tabs: Название и описание */}
                        <div className="border border-border rounded-xl overflow-hidden">
                            <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-border">
                                {/* RU */}
                                <div className="p-4 space-y-3">
                                    <h4 className="text-xs font-bold text-brand uppercase tracking-wider mb-2">{t('subcategories.rus_lang')}</h4>
                                    <div className="space-y-1">
                                        <label className={labelCls}>{t('subcategories.name', 'Название')} <span className="text-error">*</span></label>
                                        <input required {...field('name_ru')} className={inputCls} placeholder={t('subcategories.placeholder_ru', 'Название на русском')} />
                                    </div>
                                    <div className="space-y-1">
                                        <label className={labelCls}>{t('subcategories.long_name', 'Длинное название')} <span className="text-xs text-muted font-normal ml-1">({t('common.optional', 'ixtiyoriy')})</span></label>
                                        <input {...field('long_name_ru')} className={inputCls} placeholder={t('subcategories.placeholder_long_name_ru', 'Полное название для заголовка панели...')} />
                                    </div>
                                    <div className="space-y-1">
                                        <label className={labelCls}>{t('subcategories.description', 'Описание')}</label>
                                        <textarea rows={3} {...field('description_ru')} className={`${inputCls} resize-none`} placeholder={t('subcategories.placeholder_desc_ru', 'Описание на русском...')} />
                                    </div>
                                </div>
                                {/* UZ */}
                                <div className="p-4 space-y-3">
                                    <h4 className="text-xs font-bold text-brand uppercase tracking-wider mb-2">{t('subcategories.uzb_lang')}</h4>
                                    <div className="space-y-1">
                                        <label className={labelCls}>{t('subcategories.name', 'Nomi')}</label>
                                        <input {...field('name_uz')} className={inputCls} placeholder={t('subcategories.placeholder_uz', "O'zbekcha nomi")} />
                                    </div>
                                    <div className="space-y-1">
                                        <label className={labelCls}>{t('subcategories.long_name', 'Uzun nomi')} <span className="text-xs text-muted font-normal ml-1">({t('common.optional', 'ixtiyoriy')})</span></label>
                                        <input {...field('long_name_uz')} className={inputCls} placeholder={t('subcategories.placeholder_long_name_uz', "Panel sarlavhasi uchun to'liq nomi...")} />
                                    </div>
                                    <div className="space-y-1">
                                        <label className={labelCls}>{t('subcategories.description', 'Tavsif')}</label>
                                        <textarea rows={3} {...field('description_uz')} className={`${inputCls} resize-none`} placeholder={t('subcategories.placeholder_desc_uz', "O'zbekcha tavsif...")} />
                                    </div>
                                </div>
                                {/* EN */}
                                <div className="p-4 space-y-3">
                                    <h4 className="text-xs font-bold text-brand uppercase tracking-wider mb-2">{t('subcategories.eng_lang', 'In English')}</h4>
                                    <div className="space-y-1">
                                        <label className={labelCls}>{t('subcategories.name', 'Name')}</label>
                                        <input {...field('name_en')} className={inputCls} placeholder={t('subcategories.placeholder_en', 'Name in English')} />
                                    </div>
                                    <div className="space-y-1">
                                        <label className={labelCls}>{t('subcategories.long_name', 'Long title')} <span className="text-xs text-muted font-normal ml-1">({t('common.optional', 'optional')})</span></label>
                                        <input {...field('long_name_en')} className={inputCls} placeholder={t('subcategories.placeholder_long_name_en', 'Full title for panel header...')} />
                                    </div>
                                    <div className="space-y-1">
                                        <label className={labelCls}>{t('subcategories.description', 'Description')}</label>
                                        <textarea rows={3} {...field('description_en')} className={`${inputCls} resize-none`} placeholder={t('subcategories.placeholder_desc_en', 'Description in English...')} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Visiology URL */}
                        <div className="space-y-5">
                            <div className="space-y-1">
                                <label className={labelCls}>{t('subcategories.iframe_url')} (Dark Theme) <span className="text-error">*</span></label>
                                <input
                                    required
                                    {...field('visiology_url')}
                                    className={`${inputCls} ${form.visiology_url && !form.visiology_url.toLowerCase().includes('visiology') && !form.visiology_url.toLowerCase().includes('simulation.centralasian.uz') ? 'border-warning' : ''}`}
                                    placeholder={t('subcategories.iframe_placeholder_dark')}
                                />
                                {form.visiology_url && !form.visiology_url.startsWith('http://') && !form.visiology_url.startsWith('https://') && (
                                    <p className="text-xs text-error mt-1 font-medium">⚠ {t('subcategories.url_error_protocol')}</p>
                                )}
                                {form.visiology_url && (form.visiology_url.startsWith('http://') || form.visiology_url.startsWith('https://')) && !form.visiology_url.toLowerCase().includes('visiology') && !form.visiology_url.toLowerCase().includes('simulation.centralasian.uz') && (
                                    <p className="text-xs text-warning mt-1 font-medium">⚠ {t('subcategories.url_warn_visiology')}</p>
                                )}
                                <p className="text-xs text-muted mt-1">{t('subcategories.iframe_hint')}</p>
                            </div>
                            
                            <div className="space-y-1">
                                <label className={labelCls}>{t('subcategories.iframe_mobile_url')} (Dark Theme)</label>
                                <input
                                    {...field('visiology_mobile_url')}
                                    className={`${inputCls} ${form.visiology_mobile_url && !form.visiology_mobile_url.toLowerCase().includes('visiology') && !form.visiology_mobile_url.toLowerCase().includes('simulation.centralasian.uz') ? 'border-warning' : ''}`}
                                    placeholder={t('subcategories.iframe_placeholder_dark')}
                                />
                                {form.visiology_mobile_url && !form.visiology_mobile_url.startsWith('http://') && !form.visiology_mobile_url.startsWith('https://') && (
                                    <p className="text-xs text-error mt-1 font-medium">⚠ {t('subcategories.url_error_protocol')}</p>
                                )}
                                {form.visiology_mobile_url && (form.visiology_mobile_url.startsWith('http://') || form.visiology_mobile_url.startsWith('https://')) && !form.visiology_mobile_url.toLowerCase().includes('visiology') && !form.visiology_mobile_url.toLowerCase().includes('simulation.centralasian.uz') && (
                                    <p className="text-xs text-warning mt-1 font-medium">⚠ {t('subcategories.url_warn_visiology')}</p>
                                )}
                            </div>
                            
                            <hr className="border-border border-dashed my-4" />
                            
                            <div className="space-y-1">
                                <label className={labelCls}>{t('subcategories.iframe_url')} (Light Theme)</label>
                                <input
                                    {...field('visiology_url_light')}
                                    className={`${inputCls} ${form.visiology_url_light && !form.visiology_url_light.toLowerCase().includes('visiology') && !form.visiology_url_light.toLowerCase().includes('simulation.centralasian.uz') ? 'border-warning' : ''}`}
                                    placeholder={t('subcategories.iframe_placeholder_light')}
                                />
                                {form.visiology_url_light && !form.visiology_url_light.startsWith('http://') && !form.visiology_url_light.startsWith('https://') && (
                                    <p className="text-xs text-error mt-1 font-medium">⚠ {t('subcategories.url_error_protocol')}</p>
                                )}
                            </div>
                            
                            <div className="space-y-1">
                                <label className={labelCls}>{t('subcategories.iframe_mobile_url')} (Light Theme)</label>
                                <input
                                    {...field('visiology_mobile_url_light')}
                                    className={`${inputCls} ${form.visiology_mobile_url_light && !form.visiology_mobile_url_light.toLowerCase().includes('visiology') && !form.visiology_mobile_url_light.toLowerCase().includes('simulation.centralasian.uz') ? 'border-warning' : ''}`}
                                    placeholder={t('subcategories.iframe_placeholder_light')}
                                />
                                {form.visiology_mobile_url_light && !form.visiology_mobile_url_light.startsWith('http://') && !form.visiology_mobile_url_light.startsWith('https://') && (
                                    <p className="text-xs text-error mt-1 font-medium">⚠ {t('subcategories.url_error_protocol')}</p>
                                )}
                            </div>
                        </div>
                        
                        {/* Update interval */}
                        <div className="space-y-1">
                            <label className={labelCls}>{t('subcategories.update_interval')}</label>
                            <div className="flex gap-4">
                                {/* Hours Input */}
                                <div className="flex-1 flex items-stretch bg-surface-2 border border-border rounded-xl focus-within:border-brand focus-within:ring-1 focus-within:ring-brand overflow-hidden transition-all">
                                    <input
                                        type="number"
                                        min="0"
                                        value={intervalH}
                                        onChange={(e) => handleIntervalChange('h', e.target.value)}
                                        className="w-full px-4 py-2 bg-transparent border-none text-primary text-sm focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        placeholder="0"
                                    />
                                    <div className="flex flex-col border-l border-border bg-surface-2 w-8 flex-shrink-0">
                                        <button
                                            type="button"
                                            onClick={() => handleIntervalChange('h', String((intervalH || 0) + 1))}
                                            className="flex-1 flex items-center justify-center hover:bg-surface-3 transition-colors border-b border-border text-muted hover:text-primary"
                                        >
                                            <ChevronUp className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleIntervalChange('h', String(Math.max(0, (intervalH || 0) - 1)))}
                                            className="flex-1 flex items-center justify-center hover:bg-surface-3 transition-colors text-muted hover:text-primary"
                                        >
                                            <ChevronDown className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                    <div className="px-3 border-l border-border flex items-center justify-center bg-surface min-w-[70px] flex-shrink-0">
                                        <span className="text-xs text-muted font-bold tracking-wide">{isUz ? 'Soat' : 'Час'}</span>
                                    </div>
                                </div>
                                
                                {/* Minutes Input */}
                                <div className="flex-1 flex items-stretch bg-surface-2 border border-border rounded-xl focus-within:border-brand focus-within:ring-1 focus-within:ring-brand overflow-hidden transition-all">
                                    <input
                                        type="number"
                                        min="0"
                                        max="59"
                                        value={intervalM}
                                        onChange={(e) => handleIntervalChange('m', e.target.value)}
                                        className="w-full px-4 py-2 bg-transparent border-none text-primary text-sm focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        placeholder="0"
                                    />
                                    <div className="flex flex-col border-l border-border bg-surface-2 w-8 flex-shrink-0">
                                        <button
                                            type="button"
                                            onClick={() => handleIntervalChange('m', String(Math.min(59, (intervalM || 0) + 1)))}
                                            className="flex-1 flex items-center justify-center hover:bg-surface-3 transition-colors border-b border-border text-muted hover:text-primary"
                                        >
                                            <ChevronUp className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleIntervalChange('m', String(Math.max(0, (intervalM || 0) - 1)))}
                                            className="flex-1 flex items-center justify-center hover:bg-surface-3 transition-colors text-muted hover:text-primary"
                                        >
                                            <ChevronDown className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                    <div className="px-3 border-l border-border flex items-center justify-center bg-surface min-w-[70px] flex-shrink-0">
                                        <span className="text-xs text-muted font-bold tracking-wide">{isUz ? 'Minut' : 'Мин'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Users Access */}
                        <div className="space-y-2">
                            <label className={labelCls}>{t('subcategories.user_access')}</label>
                            <div className="bg-surface-2 border border-border rounded-xl p-3 max-h-48 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-1">
                                {users.map(u => {
                                    const checked = (form.user_ids || []).includes(u.id);
                                    const displayName = isUz
                                        ? [u.last_name_uz, u.first_name_uz].filter(Boolean).join(' ') || u.full_name_ru
                                        : u.full_name_ru;
                                    return (
                                        <button
                                            type="button"
                                            key={u.id}
                                            onClick={() => {
                                                setForm(f => ({
                                                    ...f,
                                                    user_ids: checked
                                                        ? (f.user_ids || []).filter(id => id !== u.id)
                                                        : [...(f.user_ids || []), u.id]
                                                }));
                                            }}
                                            className={`flex items-center gap-3 text-left px-3 py-2.5 rounded-lg transition-all
                                                ${checked ? 'bg-brand/10 border border-brand/20' : 'hover:bg-surface border border-transparent hover:border-border'}`}
                                        >
                                            <span className={`flex-shrink-0 transition-colors ${checked ? 'text-brand' : 'text-muted'}`}>
                                                {checked ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                                            </span>
                                            <div className="flex flex-col min-w-0">
                                                <span className={`text-sm font-medium leading-tight truncate ${checked ? 'text-primary' : 'text-secondary'}`}>{displayName}</span>
                                                <span className="text-[10px] text-muted">@{u.login}</span>
                                            </div>
                                        </button>
                                    );
                                })}
                                {users.length === 0 && <p className="text-xs text-muted col-span-full">{t('subcategories.users_not_found')}</p>}
                            </div>
                        </div>

                        {/* Toggles */}
                        <div className="flex flex-wrap gap-6 pt-2">
                            <Toggle label={t('subcategories.visibility')} k="is_visible" form={form} setForm={setForm} />
                            <Toggle label={t('subcategories.has_online', 'Online rejim')} k="has_online" form={form} setForm={setForm} />
                            <Toggle label={t('subcategories.has_dashboard', 'Dashboard mavjud')} k="has_dashboard" form={form} setForm={setForm} />
                        </div>

                        <div className="flex gap-3 pt-2">
                            <Button type="submit" isLoading={saving} className="w-auto px-6 py-2.5 text-sm">{t('categories.save')}</Button>
                            <button type="button" onClick={() => setShowForm(false)} className="px-6 py-2.5 rounded-xl border border-border text-secondary hover:bg-surface-2 text-sm font-semibold transition-colors">{t('categories.cancel')}</button>
                        </div>
                    </form>
                </div >
            )
            }

            {/* ── Filter + Table ── */}
            <div className="bg-surface border border-border rounded-xl overflow-hidden shadow-sm">
                <div className="px-6 py-4 border-b border-border flex items-center gap-4">
                    <label className="text-sm font-semibold text-muted flex-shrink-0">{t('subcategories.filter_by_cat')}</label>
                    <div className="w-56 flex-shrink-0">
                        <CustomSelect 
                            value={filterCat}
                            onChange={setFilterCat}
                            placeholder={t('subcategories.all')}
                            options={[
                                { value: '', label: t('subcategories.all') },
                                ...cats.map(c => ({
                                    value: c.id,
                                    label: isUz ? (c.name_uz || c.name_ru) : c.name_ru
                                }))
                            ]}
                        />
                    </div>
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            placeholder={t('search')}
                            className="w-full pl-10 pr-4 py-1.5 rounded-lg bg-surface-2 border border-border text-sm text-primary focus:outline-none focus:border-brand transition-colors"
                        />
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <table className="w-full text-left text-sm">
                            <thead className="bg-surface-2 border-b border-border text-muted uppercase text-xs">
                                <tr>
                                    <th className="px-6 py-4 font-semibold w-16"></th>
                                    <th className="px-6 py-4 font-semibold">{t('subcategories.name')}</th>
                                    <th className="px-6 py-4 font-semibold">{t('subcategories.category')}</th>
                                    <th className="px-6 py-4 font-semibold">{t('categories.creator')}</th>
                                    <th className="px-6 py-4 font-semibold">{t('categories.visibility')}</th>
                                    <th className="px-6 py-4 font-semibold">{t('subcategories.update_interval')}</th>
                                    <th className="px-6 py-4 font-semibold text-right">{t('categories.actions')}</th>
                                </tr>
                            </thead>
                            <Droppable droppableId="subcategories-list" isDropDisabled={!canEditOrder}>
                                {(provided) => (
                                    <tbody
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        className="divide-y divide-border"
                                    >
                                        {loading ? (
                                            <tr><td colSpan={5} className="px-6 py-8 text-center text-muted">{t('subcategories.loading')}</td></tr>
                                        ) : visible.length === 0 ? (
                                            <tr><td colSpan={5} className="px-6 py-8 text-center text-muted">{t('subcategories.not_found')}</td></tr>
                                        ) : visible.map((s, index) => {
                                            const cat = cats.find(c => c.id === s.category_id);
                                            const catName = cat ? (i18n.language === 'en' ? (cat.name_en || cat.name_ru || cat.name_uz) : (isUz ? (cat.name_uz || cat.name_ru) : cat.name_ru)) : '—';
                                            const hasAccess = hasAccessToSub(s);
                                            const canEditThis = currentUser?.is_superadmin || (currentUser?.can_edit_subcategories && hasAccess);
                                            const canDeleteThis = currentUser?.is_superadmin || (currentUser?.can_delete_subcategories && hasAccess);
                                            return (
                                                <Draggable key={s.id} draggableId={s.id} index={index} isDragDisabled={!canEditOrder}>
                                                    {(provided, snapshot) => (
                                                        <tr
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            className={`transition-colors ${snapshot.isDragging ? 'bg-surface-2 shadow-lg drop-shadow-xl z-50' : 'hover:bg-surface-2/50'} ${!canEditOrder ? '' : 'cursor-grab active:cursor-grabbing'} ${!hasAccess && !currentUser?.is_superadmin ? 'opacity-60' : ''}`}
                                                            style={{
                                                                ...provided.draggableProps.style,
                                                                display: snapshot.isDragging ? 'table' : '',
                                                            }}
                                                        >
                                                            <td className="px-4 py-4 w-16 text-center">
                                                                <div
                                                                    {...provided.dragHandleProps}
                                                                    className={`p-1.5 rounded-lg inline-flex items-center justify-center transition-colors ${canEditOrder ? 'text-muted hover:text-brand hover:bg-surface-2 cursor-grab active:cursor-grabbing' : 'text-border cursor-not-allowed'}`}
                                                                >
                                                                    <GripVertical className="w-5 h-5 drop-shadow-sm" />
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 font-medium">
                                                                <div className="flex items-center gap-2">
                                                                    {i18n.language === 'en' ? (s.name_en || s.name_ru || s.name_uz) : (isUz ? (s.name_uz || s.name_ru) : s.name_ru)}
                                                                    {s.is_visible && <span className="px-1.5 py-0.5 bg-success/10 text-success text-[10px] font-bold rounded">{t('subcategories.visible_status')}</span>}
                                                                    {!hasAccess && !currentUser?.is_superadmin && (
                                                                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-error/10 text-error text-[10px] font-bold rounded" title={t('subcategories.no_access')}><Lock className="w-3 h-3" /></span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 text-muted">{catName}</td>
                                                            <td className="px-6 py-4">
                                                                <span className="px-2 py-1 rounded-md bg-surface-2 border border-border text-xs text-secondary font-medium">
                                                                    @{s.created_by_login || t('subcategories.system_user')}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4 text-center">
                                                                {(!canEditThis) ? (
                                                                    <div className={`flex items-center gap-1.5 w-fit text-[11px] font-bold px-2.5 py-1 rounded-full mx-auto
                                                                        ${s.is_visible ? 'bg-success/10 text-success' : 'bg-surface-2 text-muted'}`}>
                                                                        {s.is_visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                                                                        {s.is_visible ? t('categories.active') : t('categories.cancel')}
                                                                    </div>
                                                                ) : (
                                                                    <button
                                                                        onClick={() => toggleVisible(s)}
                                                                        disabled={togglingId === s.id}
                                                                        className={`flex items-center gap-1.5 w-fit text-[11px] font-bold px-2.5 py-1 rounded-full transition-colors mx-auto
                                                                        ${s.is_visible ? 'bg-success/10 text-success hover:bg-success/20' : 'bg-surface-2 text-muted hover:bg-surface-3'}
                                                                        disabled:opacity-50 disabled:cursor-not-allowed`}>
                                                                        {togglingId === s.id ? (
                                                                            <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                                                        ) : (
                                                                            s.is_visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />
                                                                        )}
                                                                        {s.is_visible ? t('categories.active') : t('categories.cancel')}
                                                                    </button>
                                                                    )}
                                                                </td>
                                                                <td className="px-6 py-4 font-medium whitespace-nowrap">
                                                                    {s.update_interval ? (
                                                                        <span className="px-2.5 py-1 rounded-md bg-brand/10 text-brand text-xs font-bold border border-brand/20">
                                                                            {formatInterval(s.update_interval)}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-muted text-xs">—</span>
                                                                    )}
                                                                </td>
                                                                <td className="px-6 py-4 text-right space-x-2">
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        navigate(`/admin/subcategories/${s.id}/history`);
                                                                    }}
                                                                    className="p-2 text-secondary hover:text-brand bg-surface-2 rounded-lg transition-colors"
                                                                    title={t('users.history_title')}
                                                                >
                                                                    <History className="w-4 h-4" />
                                                                </button>
                                                                <button
                                                                    onClick={() => openEdit(s)}
                                                                    disabled={!canEditThis}
                                                                    className="p-2 text-secondary hover:text-brand bg-surface-2 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                                                    title={!canEditThis ? t('subcategories.no_access') : ''}
                                                                >
                                                                    <Edit className="w-4 h-4" />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    disabled={!canDeleteThis}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        handleDelete(s.id);
                                                                    }}
                                                                    className="p-2 text-secondary hover:text-error bg-surface-2 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                                                    title={!canDeleteThis ? t('subcategories.no_access') : t('categories.delete')}
                                                                >
                                                                    <Trash2 className="w-4 h-4 pointer-events-none" />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}
                                    </tbody>
                                )}
                            </Droppable>
                        </table>
                    </DragDropContext>
                </div>
            </div>

            <ConfirmModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onConfirm={confirmDelete}
                isLoading={isDeleting}
                title={t('subcategories.delete_confirm_title')}
                message={t('subcategories.delete_confirm_msg')}
            />
        </div >
    );
}
